import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Archive, ArchiveRestore, CalendarRange, ChevronDown, LogOut, Map, Plus, RadioTower, UsersRound } from 'lucide-react'
import { api, ApiError } from './api'
import { Button, EmptyState, Field, IconButton, Loading, Modal, Notice, Pill, TextInput } from './components'
import { EventsPage } from './EventsPage'
import { Login } from './Login'
import { PeoplePage } from './PeoplePage'
import { PublishPage } from './PublishPage'
import type { Project, TemplateCatalog, User } from './types'
import { takeInitialMinecraftHandoffFragment } from './handoff'
import { WorldPage } from './WorldPage'

type Page = 'people' | 'world' | 'events' | 'publish'

export default function App() {
  const [user, setUser] = useState<User | null>()
  const [projects, setProjects] = useState<Project[]>([])
  const [projectId, setProjectId] = useState(window.localStorage.getItem('extranpcs-project') ?? '')
  const [templates, setTemplates] = useState<TemplateCatalog>()
  const [page, setPage] = useState<Page>('people')
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)
  const [projectActionBusy, setProjectActionBusy] = useState(false)
  const [handoffConnectionId, setHandoffConnectionId] = useState<string | null>(null)
  const handoffAttempt = useRef<Promise<{ user: User; csrfToken: string; projectId: string | null; connectionId: string | null }> | null>(null)
  const handoffTicket = takeInitialMinecraftHandoffFragment()

  const bootstrap = useCallback(async (preferredProjectId?: string | null) => {
    try {
      setError('')
      const signedIn = await api.me()
      setUser(signedIn)
      const [availableProjects, catalog] = await Promise.all([api.projects(), api.templates()])
      setProjects(availableProjects)
      setTemplates(catalog)
      setProjectId((current) => {
        if (preferredProjectId && availableProjects.some((project) => project.id === preferredProjectId)) {
          return preferredProjectId
        }
        return availableProjects.some((project) => project.id === current)
          ? current : availableProjects[0]?.id ?? ''
      })
    } catch (reason) {
      if (reason instanceof ApiError && reason.status === 401) setUser(null)
      else setError(reason instanceof Error ? reason.message : 'Studio could not start')
    }
  }, [])

  useEffect(() => {
    let active = true
    const ticket = handoffTicket
    if (!ticket) {
      void bootstrap()
      return () => { active = false }
    }
    void (async () => {
      try {
        handoffAttempt.current ??= api.consumeMinecraftHandoff(ticket)
        const result = await handoffAttempt.current
        if (active) {
          setHandoffConnectionId(result.connectionId)
          await bootstrap(result.projectId)
        }
      } catch {
        if (!active) return
        setUser(null)
        setError('This Studio handoff link is invalid or has expired. Run /extranpcs studio again.')
      }
    })()
    return () => { active = false }
  }, [bootstrap, handoffTicket])
  useEffect(() => {
    if (projectId) window.localStorage.setItem('extranpcs-project', projectId)
  }, [projectId])

  const project = useMemo(() => projects.find((item) => item.id === projectId), [projectId, projects])

  async function createProject(name: string) {
    const created = await api.createProject(name)
    setProjects((current) => [created, ...current])
    setProjectId(created.id)
    setCreating(false)
  }

  async function updateProjectState(state: 'draft' | 'archived') {
    if (!project || projectActionBusy) return
    if (state === 'archived' && !window.confirm(`Archive ${project.name}? Its content will become read-only until an administrator restores it.`)) return
    setProjectActionBusy(true)
    setError('')
    try {
      const updated = await api.updateProject(project.id, { state })
      setProjects(current => current.map(item => item.id === updated.id ? updated : item))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Project state could not be changed')
    } finally {
      setProjectActionBusy(false)
    }
  }

  if (user === undefined) return <main className="boot"><img src="/brand/extranpcs-square.svg" alt="" /><Loading label="Opening Studio" />{error && <Notice kind="error">{error}</Notice>}</main>
  if (!user) return <Login onAuthenticated={() => void bootstrap()} notice={error} />
  if (!templates) return <main className="boot"><Loading label="Loading authoring tools" /></main>
  const authoringUser: User = project?.state === 'archived' ? { ...user, role: 'viewer' } : user

  return (
    <div className="studio-shell">
      <header className="topbar">
        <a href="#main" className="skip-link">Skip to editor</a>
        <div className="wordmark"><img src="/brand/extranpcs-horizontal-dark.svg" alt="ExtraNPCs Studio" /></div>
        <div className="project-switcher">
          {projects.length > 0 ? (
            <label><span className="sr-only">Current project</span><select value={projectId} onChange={(event) => setProjectId(event.target.value)}>{projects.map((item) => <option key={item.id} value={item.id}>{item.name}{item.state === 'archived' ? ' (archived)' : ''}</option>)}</select><ChevronDown size={15} aria-hidden="true" /></label>
          ) : <span>No project</span>}
          {project && <Pill tone={project.state === 'published' ? 'green' : project.state === 'archived' ? 'red' : 'yellow'}>{project.state.replace('_', ' ')}</Pill>}
          <IconButton label="Create project" disabled={user.role === 'viewer'} onClick={() => setCreating(true)}><Plus size={17}/></IconButton>
          {project && user.role === 'admin' && (project.state === 'archived'
            ? <IconButton label="Restore current project to draft" disabled={projectActionBusy} onClick={() => void updateProjectState('draft')}><ArchiveRestore size={17}/></IconButton>
            : <IconButton label="Archive current project" disabled={projectActionBusy} onClick={() => void updateProjectState('archived')}><Archive size={17}/></IconButton>)}
        </div>
        <div className="account">
          <div><strong>{user.email.split('@')[0]}</strong><span>{user.role}</span></div>
          <button type="button" onClick={async () => { await api.logout(); setUser(null) }} aria-label="Sign out" title="Sign out"><LogOut size={18} /></button>
        </div>
      </header>
      <nav className="primary-nav" aria-label="Studio sections">
        <NavButton active={page === 'people'} onClick={() => setPage('people')} icon={<UsersRound />} label="People" />
        <NavButton active={page === 'world'} onClick={() => setPage('world')} icon={<Map />} label="World" />
        <NavButton active={page === 'events'} onClick={() => setPage('events')} icon={<CalendarRange />} label="Events" />
        <NavButton active={page === 'publish'} onClick={() => setPage('publish')} icon={<RadioTower />} label="Publish" />
      </nav>
      <main id="main" className="studio-main">
        {error && <Notice kind="error" onDismiss={() => setError('')}>{error}</Notice>}
        {project?.state === 'archived' && <Notice kind="info">This project is archived and read-only. An administrator can restore it to a draft from the project controls.</Notice>}
        {!project ? (
          <EmptyState icon={<Map size={34} />} title="Create the first living-city project">
            A project keeps drafts, immutable assets, dialogue versions, world editor data, and complete published bundles together.
            <Button onClick={() => setCreating(true)}>Create project</Button>
          </EmptyState>
        ) : page === 'people' ? (
          <PeoplePage project={project} user={authoringUser} templates={templates} />
        ) : page === 'world' ? (
          <WorldPage project={project} user={authoringUser} templates={templates} preferredConnectionId={handoffConnectionId} />
        ) : page === 'events' ? (
          <EventsPage project={project} user={authoringUser} templates={templates} />
        ) : (
          <PublishPage project={project} user={authoringUser} betterHudTemplates={templates.betterHudTemplates} preferredConnectionId={handoffConnectionId} onProjectRefresh={async () => setProjects(await api.projects())} />
        )}
      </main>
      {creating && <CreateProject onCancel={() => setCreating(false)} onCreate={createProject} />}
    </div>
  )
}

function NavButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: ReactNode; label: string }) {
  return <button type="button" className={active ? 'active' : ''} aria-current={active ? 'page' : undefined} onClick={onClick}>{icon}<span>{label}</span></button>
}

function CreateProject({ onCancel, onCreate }: { onCancel: () => void; onCreate: (name: string) => Promise<void> }) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const formId = 'create-project-form'
  return <Modal title="Name the project" onClose={onCancel} footer={<><Button variant="quiet" type="button" onClick={onCancel}>Cancel</Button><Button type="submit" form={formId} disabled={busy || !name.trim()}>{busy ? 'Creating…' : 'Create project'}</Button></>}><form id={formId} onSubmit={async (event) => { event.preventDefault(); setBusy(true); try { await onCreate(name) } finally { setBusy(false) } }}><Field label="Project name" help="The stable project ID is generated separately and never changes."><TextInput data-modal-initial-focus value={name} onChange={(event) => setName(event.target.value)} required maxLength={120} /></Field></form></Modal>
}
